<template>
  <div class="d-flex w-full flex-column h-screen bg-surface-secondary overflow-hidden">
    <vue-element-loading :active="loading" :is-full-screen="true" spinner="ring" color="#5c60f5" />

    <main class="flex-fill w-100 min-w-0 overflow-y-auto p-6 p-lg-10 custom-scrollbar pb-32">
      <div class="container-fluid px-0 w-100">
        <header class="mb-8 d-flex justify-content-between align-items-end">
          <div>
            <h1 class="h3 ls-tight mb-1">Multi-Ancestry Genomic Pipeline</h1>
            <p class="text-sm text-muted mb-0">
              Build multiple GWAS+LD plans, then submit algorithms in batch.
            </p>
          </div>
          <div class="hstack gap-3">
            <router-link class="btn btn-sm btn-neutral" to="/tutorial">
              <i class="bi bi-question-circle me-2"></i>Guide
            </router-link>
          </div>
        </header>

        <div class="vstack gap-8">
          <StepDataIngestion
            :gwasKeyword="gwasKeyword"
            :loadingGwas="loadingGwas"
            :gwasError="gwasError"
            :gwasFiles="gwasFiles"
            :gwasFileId="gwasFileId"
            :gwasSelectedLabel="gwasSelectedLabel"
            :gwasTotal="gwasTotal"
            :gwasPage="gwasPage"
            :gwasMaxPage="gwasMaxPage"

            :ldKeyword="ldKeyword"
            :loadingLd="loadingLd"
            :ldError="ldError"
            :ldFiles="ldFiles"
            :fileLDId="fileLDId"
            :ldSelectedLabel="ldSelectedLabel"
            :ldTotal="ldTotal"
            :ldPage="ldPage"
            :ldMaxPage="ldMaxPage"

            :ldSource="ldSource"
            :population="officialPopulation"
            :officialLdBase="OFFICIAL_LD_BASE"

            @update:gwasKeyword="gwasKeyword = $event"
            @update:ldKeyword="ldKeyword = $event"
            @update:ldSource="ldSource = $event"
            @update:population="officialPopulation = $event"

            @refresh-gwas="fetchGwasFiles"
            @refresh-ld="fetchLdFiles"
            @gwas-prev="gwasPrev"
            @gwas-next="gwasNext"
            @ld-prev="ldPrev"
            @ld-next="ldNext"
            @select-gwas="selectGwas"
            @select-ld="selectLd"
          />

          <div class="d-flex justify-content-center">
            <button class="btn btn-sm btn-primary rounded-pill px-4" @click="addPlan" :disabled="!canAddPlan">
              <i class="bi bi-plus-circle me-2"></i>Add Plan (GWAS + LD)
            </button>
          </div>

          <section class="pipeline-section">
            <div class="d-flex align-items-center gap-3 mb-3">
              <span class="step-indicator">01B</span>
              <div class="min-w-0">
                <h2 class="h6 mb-0">Plans</h2>
                <p class="text-2xs text-muted mb-0">Click a plan to make it active for parameter preview.</p>
              </div>
              <div class="ms-auto">
                <span class="badge bg-soft-primary text-primary rounded-pill px-3">{{ plans.length }} Plan(s)</span>
              </div>
            </div>

            <div v-if="plans.length === 0" class="alert alert-light border mb-0">
              No plan yet. Select GWAS & LD above, then click <b>Add Plan</b>.
            </div>

            <div v-else class="border rounded-3 bg-white shadow-sm overflow-hidden">
              <table class="table table-hover table-borderless align-middle mb-0">
                <thead class="bg-surface-tertiary border-bottom">
                  <tr>
                    <th class="ps-4 py-3 text-2xs text-uppercase text-muted font-bold" style="width: 64px;">Active</th>
                    <th class="py-3 text-2xs text-uppercase text-muted font-bold">GWAS</th>
                    <th class="py-3 text-2xs text-uppercase text-muted font-bold">LD</th>
                    <th class="py-3 text-2xs text-uppercase text-muted font-bold text-end pe-4" style="width: 90px;">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(p, idx) in plans"
                    :key="p.id"
                    :class="{ 'bg-blue-50': idx === activePlanIndex }"
                    @click="setActivePlan(idx)"
                    style="cursor:pointer;"
                  >
                    <td class="ps-4">
                      <i v-if="idx === activePlanIndex" class="bi bi-check-circle-fill text-primary"></i>
                      <i v-else class="bi bi-circle text-muted"></i>
                    </td>
                    <td class="text-truncate">{{ p.gwasLabel }}</td>
                    <td class="text-truncate">{{ p.ldLabel }}</td>
                    <td class="text-end pe-4">
                      <button class="btn btn-sm btn-outline-danger" @click.stop="removePlan(idx)">
                        <i class="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <div class="pipeline-flow-line"></div>

          <section class="pipeline-section">
            <div class="d-flex align-items-center gap-3 mb-4">
              <span class="step-indicator">02</span>
              <div>
                <h2 class="h5 mb-0">Algorithm Selection</h2>
                <p class="text-2xs text-muted mb-0">Enable modules to configure them below.</p>
              </div>
            </div>

            <div v-if="algoError" class="alert alert-danger py-2 text-sm">
              <i class="bi bi-exclamation-triangle me-1"></i>{{ algoError }}
            </div>

            <div class="border rounded-3 bg-white shadow-sm overflow-hidden">
              <table class="table table-hover table-borderless align-middle mb-0">
                <thead class="bg-surface-tertiary border-bottom">
                  <tr>
                    <th class="ps-6 py-3 text-2xs text-uppercase text-muted font-bold" style="width: 80px;">Enable</th>
                    <th class="py-3 text-2xs text-uppercase text-muted font-bold">Module Name</th>
                    <th class="py-3 text-2xs text-uppercase text-muted font-bold">Core Capability</th>
                    <th class="py-3 text-2xs text-uppercase text-muted font-bold text-end pe-6">Type</th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="algo in algorithms.algorithmsShow"
                    :key="algo.id"
                    :class="{ 'bg-blue-50': isAlgoSelected(algo.id) }"
                  >
                    <td class="ps-6">
                      <div class="form-check form-switch">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          :checked="isAlgoSelected(algo.id)"
                          @change="toggleAlgorithm($event, algo)"
                        >
                      </div>
                    </td>
                    <td>
                      <div class="d-flex align-items-center">
                        <span class="font-bold text-dark me-2">{{ algo.name }}</span>
                        <code class="text-2xs text-primary bg-soft-primary px-1 rounded">{{ algo.code }}</code>
                      </div>
                    </td>
                    <td>
                      <p class="text-xs text-muted mb-0 text-truncate" style="max-width: 400px;">
                        {{ algo.description }}
                      </p>
                    </td>
                    <td class="text-end pe-6">
                      <span class="badge badge-dot">
                        <i class="bg-primary"></i><span class="text-2xs text-muted ms-2">Core</span>
                      </span>
                    </td>
                  </tr>

                  <tr v-if="!algorithms.algorithmsShow || algorithms.algorithmsShow.length === 0">
                    <td colspan="4" class="py-4 text-center text-muted">
                      No algorithms loaded. Check PocketBase collection <code>algorithms</code>.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </main>

    <BottomParameterPanel
      :algorithmsData="algorithmsData"
      :activeAlgoId="activeAlgoId"
      :jobName="jobName"
      :submitFlag="submitFlag"
      :submitDisabledReason="submitDisabledReason"
      :currentAlgoParams="currentAlgoParams"
      :cromwellInputPreview="cromwellInputPreview"
      :isModifiedFn="isModified"
      :isParamTouchedFn="isParamTouched"
      :stringifyDefaultFn="stringifyDefault"
      :prettyJsonFn="prettyJson"
      @update:activeAlgoId="activeAlgoId = $event"
      @update:jobName="jobName = $event"
      @save-params="onSaveParameters"
      @submit="dataSubmit"
    />
  </div>
</template>

<script>
import { isEmpty } from "@/utils/validate";
import { pb } from "@/utils/pocketbase";
import { listFiles } from "@/api/storage";

import StepDataIngestion from "@/components/pipeline/StepDataIngestionmulti.vue";
import BottomParameterPanel from "@/components/pipeline/BottomParameterPanel.vue";

import {
  parseJsonLike,
  statusBadgeClass,
  loadFileList,
  normalizeValueForCromwell,
  prettyJson,
  stringifyDefault,
} from "@/utils/pipelineHelpers";

export default {
  name: "MultipleAncestriesItem",
  components: { StepDataIngestion, BottomParameterPanel },

  data() {
    return {
      OFFICIAL_LD_BASE: "/pmaster/chenxingyu/chenxy/project/10algorithm/offical_ldref/LD_reference",

      loading: false,
      algoError: "",

      algorithms: { algorithmsShow: [] },
      activeAlgoId: null,

      gwasFileId: null,
      gwasFileKey: null,
      gwasSelectedLabel: "",

      fileLDId: null,
      ldFileKey: null,
      ldSelectedLabel: "",

      ldSource: "uploaded",
      officialPopulation: "",
      officialLdPath: null,

      plans: [],
      activePlanIndex: -1,

      jobName: `ANALYSIS_${new Date().toISOString().slice(0, 10).replace(/-/g, "_")}`,

      gwasFiles: [],
      gwasTotal: 0,
      gwasPage: 1,
      gwasPageSize: 6,
      gwasKeyword: "",
      loadingGwas: false,
      gwasError: "",

      ldFiles: [],
      ldTotal: 0,
      ldPage: 1,
      ldPageSize: 6,
      ldKeyword: "",
      loadingLd: false,
      ldError: "",

      // ✅ 记住当前 LD 的 ancestry（uploaded 时用于 pop_0）
      currentLdAncestry: "",
    };
  },

  computed: {
    algorithmsData() {
      return this.$store.getters["algorithmsData/getAlgorithmsData"];
    },

    currentAlgoParams() {
      if (!this.activeAlgoId) return [];
      const algo = this.algorithms.algorithmsShow.find((a) => a.id === this.activeAlgoId);
      return algo ? algo.parameters : [];
    },

    gwasMaxPage() {
      return Math.max(1, Math.ceil((this.gwasTotal || 0) / this.gwasPageSize));
    },

    ldMaxPage() {
      return Math.max(1, Math.ceil((this.ldTotal || 0) / this.ldPageSize));
    },

    canAddPlan() {
      if (!this.gwasFileId || !this.gwasFileKey) return false;
      if (this.ldSource === "official") return !!this.officialLdPath;
      return !!this.fileLDId && !!this.ldFileKey;
    },

    submitFlag() {
      if (this.loading) return true;
      if (this.algorithmsData.length === 0) return true;
      if (this.plans.length === 0) return true;
      if (isEmpty(this.jobName)) return true;
      return false;
    },

    submitDisabledReason() {
      if (this.loading) return "Submitting...";
      if (this.plans.length === 0) return "Please add at least one plan";
      if (this.algorithmsData.length === 0) return "Please enable at least one algorithm";
      if (isEmpty(this.jobName)) return "Job ID is empty";
      return "";
    },

    cromwellInputPreview() {
      return this.buildCromwellInputsPreview();
    },
  },

  methods: {
    statusBadgeClass,

    fetchGwasFiles() {
      return loadFileList({
        listFiles,
        category: "gwas",
        page: this.gwasPage,
        pageSize: this.gwasPageSize,
        keyword: this.gwasKeyword,
        setLoading: (v) => (this.loadingGwas = v),
        setError: (m) => (this.gwasError = m),
        setList: (arr) => (this.gwasFiles = arr),
        setTotal: (n) => (this.gwasTotal = n),
      });
    },

    fetchLdFiles() {
      return loadFileList({
        listFiles,
        category: "LD",
        page: this.ldPage,
        pageSize: this.ldPageSize,
        keyword: this.ldKeyword,
        setLoading: (v) => (this.loadingLd = v),
        setError: (m) => (this.ldError = m),
        setList: (arr) => (this.ldFiles = arr),
        setTotal: (n) => (this.ldTotal = n),
      });
    },

    async turnPage(type, delta) {
      const isGwas = type === "gwas";
      const pageKey = isGwas ? "gwasPage" : "ldPage";
      const maxPage = isGwas ? this.gwasMaxPage : this.ldMaxPage;

      const next = this[pageKey] + delta;
      if (next < 1 || next > maxPage) return;
      this[pageKey] = next;

      if (isGwas) return this.fetchGwasFiles();
      return this.fetchLdFiles();
    },

    gwasPrev() { return this.turnPage("gwas", -1); },
    gwasNext() { return this.turnPage("gwas", +1); },
    ldPrev() { return this.turnPage("ld", -1); },
    ldNext() { return this.turnPage("ld", +1); },

    selectGwas(f) {
      this.gwasFileId = f.file_id;
      this.gwasFileKey = f.storage_key || null;
      this.gwasSelectedLabel = `${f.display_name} (${f.ancestry || "NA"})`;
    },

    selectLd(payload) {
      if (payload && payload.source === "official") {
        this.ldSource = "official";
        this.officialPopulation = payload.population || "";
        this.officialLdPath = payload.ldref_0 || null;
        this.ldSelectedLabel = payload.display_name || `Official ${payload.population || ""}`.trim();
        this.fileLDId = "__official__";
        this.ldFileKey = null;

        // ✅ official 的 pop 用 population
        this.currentLdAncestry = this.officialPopulation || "";
        return;
      }

      if (payload && payload.file_id) {
        this.ldSource = "uploaded";
        this.officialPopulation = "";
        this.officialLdPath = null;

        this.fileLDId = payload.file_id;
        this.ldFileKey = payload.storage_key || null;
        this.ldSelectedLabel = `${payload.display_name} (${payload.ancestry || "NA"})`;

        // ✅ uploaded 的 pop 用 ancestry
        this.currentLdAncestry = payload.ancestry || "";
        return;
      }

      this.fileLDId = null;
      this.ldFileKey = null;
      this.officialLdPath = null;
      this.ldSelectedLabel = "";
      this.currentLdAncestry = "";
    },

    addPlan() {
      if (!this.canAddPlan) return;

      // ✅ multi: plan 里 ldKey 我们存 “population”(如 EUR/AFR)；真正 ldref_0 固定用 OFFICIAL_LD_BASE
      const pop = (this.ldSource === "official")
        ? (this.officialPopulation || "")
        : (this.currentLdAncestry || "");

      // GWAS key（S3 key）
      const gwasKey = this.gwasFileKey;

      // 这里用 gwasKey + pop 做唯一键（避免重复 plan）
      const uniqKey = `${gwasKey}||${pop}`;
      if (this.plans.some((p) => `${p.gwasKey}||${p.pop}` === uniqKey)) {
        this.$message?.warning?.("This plan already exists.");
        return;
      }

      const plan = {
        id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        gwasFileId: this.gwasFileId,
        gwasKey,
        gwasLabel: this.gwasSelectedLabel || "GWAS",

        // 仅用于展示/回显
        ldSource: this.ldSource,
        ldFileId: this.fileLDId,

        // ✅ 关键：multi 下 ldKey 不再是路径，直接用 pop（EUR/AFR）
        ldKey: pop,

        // 展示 label
        ldLabel:
          (this.ldSource === "official")
            ? (this.ldSelectedLabel || `Official ${pop}`.trim())
            : (this.ldSelectedLabel || `LD (${pop || "NA"})`),

        // ✅ pop_0 用
        pop,
      };

      this.plans.push(plan);
      this.setActivePlan(this.plans.length - 1);

      // ✅ 让已选算法立即刷新到 array 形态
      (this.algorithmsData || []).forEach((m) => this.onSaveParameters(m.id));
    },

    removePlan(idx) {
      if (idx < 0 || idx >= this.plans.length) return;
      this.plans.splice(idx, 1);

      if (this.plans.length === 0) {
        this.activePlanIndex = -1;
        // ✅ 清空后也同步一次（避免还显示旧 array）
        (this.algorithmsData || []).forEach((m) => this.onSaveParameters(m.id));
        return;
      }

      const next = Math.min(idx, this.plans.length - 1);
      this.setActivePlan(next);
    },

    setActivePlan(idx) {
      this.activePlanIndex = idx;
      // activePlan 只用于 preview，不再用于覆盖 inputs
      (this.algorithmsData || []).forEach((m) => this.onSaveParameters(m.id));
    },

    getActivePlan() {
      if (this.activePlanIndex < 0 || this.activePlanIndex >= this.plans.length) return null;
      return this.plans[this.activePlanIndex];
    },

    async fetchAlgorithmsFromPB() {
      try {
        this.loading = true;
        this.algoError = "";

        const list = await pb.collection("algorithms").getFullList({
          sort: "name",
          filter: `status = "active" && type = "multi"`,
        });

        this.algorithms.algorithmsShow = list.map((a) => {
          const schema = parseJsonLike(a.input_schema, {});
          const defaults = parseJsonLike(a.defaults, {});

          const parameters =
            schema && schema.properties
              ? this.buildParamsFromJsonSchema(a, schema, defaults)
              : this.buildParamsFromFlatCromwellConfig(a, schema);

          return {
            id: a.id,
            code: a.code?.toLowerCase(),
            name: a.name,
            description: a.description || "",
            parameters,
          };
        });

      } catch (e) {
        console.error("[Error] fetchAlgorithmsFromPB:", e);
        this.algoError = e?.message || "Failed to load algorithms";
      } finally {
        this.loading = false;
      }
    },

    isAlgoSelected(id) {
      return this.algorithmsData.some((a) => a.id === id);
    },

    isParamTouched(p) {
      if (p.isArray) return Array.isArray(p.setValue) && p.setValue.length > 0;
      return p.setValue !== "" && p.setValue !== null && p.setValue !== undefined;
    },

    isModified(algoId) {
      const algo = this.algorithms.algorithmsShow.find((a) => a.id === algoId);
      return !!algo && algo.parameters.some((p) => this.isParamTouched(p));
    },

    toggleAlgorithm(e, algo) {
      if (e.target.checked) {
        this.activeAlgoId = algo.id;
        this.onSaveParameters(algo.id);
      } else {
        this.$store.dispatch("algorithmsData/deleteAlgorithmsData", algo.id);
        if (this.activeAlgoId === algo.id) {
          this.activeAlgoId = this.algorithmsData.length > 0 ? this.algorithmsData[0].id : null;
        }
      }
    },

    buildParamsFromJsonSchema(a, schema, defaults) {
      const props = schema.properties || {};
      const requiredList = Array.isArray(schema.required) ? schema.required : [];

      return Object.keys(props).map((key, idx) => {
        const def = defaults?.[key] ?? props[key]?.default ?? "";
        const propType = props[key]?.type || "";
        const enumList = Array.isArray(props[key]?.enum) ? props[key].enum : [];
        const isArray = propType === "array" || Array.isArray(def);

        return {
          id: `${a.id}_${idx}_${key}`,
          wdlKey: key,
          displayName: key,
          description: props[key]?.description || "",
          defaultValue: def,
          setValue: isArray ? [] : "",
          schemaType: propType || (isArray ? "array" : "string"),
          isArray,
          enum: enumList,
          required: requiredList.includes(key),
        };
      });
    },

    buildParamsFromFlatCromwellConfig(a, cfg) {
      const parameters = [];
      const keys = Object.keys(cfg || {});
      const isMetaKey = (k) => k.endsWith("_description") || k.endsWith("_type");

      keys.forEach((k) => {
        if (!k.endsWith("_value")) return;

        const base = k.slice(0, -"_value".length);
        const desc = cfg[`${base}_description`] || "";
        const typ = String(cfg[`${base}_type`] || "").toLowerCase();

        const rawVal = cfg[k];
        const isArray = Array.isArray(rawVal);
        const enumList = isArray ? rawVal : [];

        let schemaType = "string";
        if (typ === "float" || typ === "number") schemaType = "number";
        if (typ === "int" || typ === "integer") schemaType = "integer";
        if (typ === "boolean" || typ === "bool") schemaType = "boolean";
        if (isArray) schemaType = "array";

        parameters.push({
          id: `${a.id}_${base}`,
          wdlKey: k,
          displayName: base.split(".").pop(),
          description: desc,
          defaultValue: rawVal,
          setValue: isArray ? [] : "",
          schemaType,
          isArray,
          enum: enumList,
          required: false,
        });
      });

      keys.forEach((k) => {
        if (k.endsWith("_value")) return;
        if (isMetaKey(k)) return;
        if (parameters.some((p) => p.wdlKey === k)) return;

        const rawVal = cfg[k];
        const isArray = Array.isArray(rawVal);

        let schemaType = "string";
        if (typeof rawVal === "number") schemaType = "number";
        if (typeof rawVal === "boolean") schemaType = "boolean";
        if (isArray) schemaType = "array";

        parameters.push({
          id: `${a.id}_${k}`,
          wdlKey: k,
          displayName: k.split(".").pop(),
          description: "",
          defaultValue: rawVal,
          setValue: isArray ? [] : "",
          schemaType,
          isArray,
          enum: [],
          required: false,
        });
      });

      parameters.sort((x, y) => x.wdlKey.localeCompare(y.wdlKey));
      return parameters;
    },

    // ✅ multi 注入：summary_statistic_2 => Array[String], pop_0 => Array[String], ldref_0 固定 base
    injectPlansIntoAlgo(algo) {
      if (!algo || !Array.isArray(algo.parameters)) return;

      const gwasArr = (this.plans || []).map((p) => p.gwasKey).filter(Boolean);
      const popArr = (this.plans || []).map((p) => p.pop).filter(Boolean);

      const ldBase = this.OFFICIAL_LD_BASE;

      algo.parameters.forEach((p) => {
        const k = String(p.wdlKey || "");
        if (!k) return;

        if (k.includes("summary_statistic_2")) {
          this.$set(p, "isArray", true);
          this.$set(p, "schemaType", "array");
          this.$set(p, "defaultValue", gwasArr);
          this.$set(p, "setValue", gwasArr);
          return;
        }

        if (k.includes(".pop_0") || k.endsWith("pop_0")) {
          this.$set(p, "isArray", true);
          this.$set(p, "schemaType", "array");
          this.$set(p, "defaultValue", popArr);
          this.$set(p, "setValue", popArr);
          return;
        }

        if (k.includes("ldref_0")) {
          this.$set(p, "defaultValue", ldBase);
          this.$set(p, "setValue", ldBase);
        }
      });
    },

    onSaveParameters(algoId) {
      const algorithm = this.algorithms.algorithmsShow.find((a) => a.id === algoId);
      if (!algorithm) return;

      // ✅ multi: 只要 plans 存在，就注入 array；不再用 activePlan 覆盖
      if (this.plans && this.plans.length > 0) {
        this.injectPlansIntoAlgo(algorithm);
      }

      const kvParams = {};
      algorithm.parameters.forEach((p) => {
        const finalVal = p.isArray
          ? ((Array.isArray(p.setValue) && p.setValue.length > 0) ? p.setValue : p.defaultValue)
          : ((p.setValue === "" || p.setValue === null || p.setValue === undefined) ? p.defaultValue : p.setValue);

        kvParams[p.wdlKey] = normalizeValueForCromwell(p, finalVal);
      });

      this.$store.dispatch("algorithmsData/addAlgorithmsData", {
        id: algorithm.id,
        code: algorithm.code,
        name: algorithm.name,
        parameters: kvParams,
      });
    },

    buildCromwellInputsPreview() {
      const merged = {};
      (this.algorithmsData || []).forEach((m) => {
        if (!m?.parameters) return;
        Object.keys(m.parameters).forEach((k) => (merged[k] = m.parameters[k]));
      });
      return merged;
    },

    prettyJson,
    stringifyDefault,

    // ✅ multi: 一次提交=每个算法 1 个 run（inputs 里带 array）
    async dataSubmit() {
      if (!pb.authStore.isValid) {
        this.$message?.error?.("Please login first.");
        return;
      }

      try {
        this.loading = true;

        const owner = pb.authStore.model.id;
        const created = [];
        const submitted = [];

        const baseUrl = (pb.baseUrl || "http://192.168.77.22:8090").replace(/\/+$/, "");
        const token = pb.authStore.token;

        // ✅ 提交前把所有已选算法刷新一次（确保 array 写进 store）
        (this.algorithmsData || []).forEach((m) => this.onSaveParameters(m.id));

        for (const m of (this.algorithmsData || [])) {
          const algo = this.algorithms.algorithmsShow.find((a) => a.id === m.id);
          if (!algo) continue;

          // 再保险：注入一次
          if (this.plans && this.plans.length > 0) this.injectPlansIntoAlgo(algo);

          const kv = {};
          (algo.parameters || []).forEach((p) => {
            const finalVal = p.isArray
              ? ((Array.isArray(p.setValue) && p.setValue.length > 0) ? p.setValue : p.defaultValue)
              : ((p.setValue === "" || p.setValue === null || p.setValue === undefined) ? p.defaultValue : p.setValue);
            kv[p.wdlKey] = normalizeValueForCromwell(p, finalVal);
          });

          const payload = {
            owner,
            algorithm: m.id,
            engine: "cromwell",
            status: "draft",
            params: kv,
            inputs_json: { ...kv },
            job_name: `${this.jobName}__${(algo.code || "algo").toUpperCase()}`,
          };

          const rec = await pb.collection("runs").create(payload);
          created.push(rec);

          const runId = rec.id;
          const submitUrl = `${baseUrl}/api/runs/${runId}/submit`;

          const resp = await fetch(submitUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({}),
          });

          const out = await resp.json().catch(() => ({}));
          if (!resp.ok) {
            this.$message?.error?.(`Run ${runId} submit failed: ${out?.message || resp.status}`);
            continue;
          }
          submitted.push({ runId, ...out });
        }

        this.$message?.success?.(`Created ${created.length} run(s), submitted ${submitted.length} run(s)!`);
        this.$router.push({ name: "statistics" });
        return { created, submitted };

      } catch (e) {
        console.error("[Error] submit failed:", e);
        this.$message?.error?.(e?.message || "Submit failed");
      } finally {
        this.loading = false;
      }
    },
  },

  async mounted() {
    this.$store.dispatch("algorithmsData/deleteAllAlgorithmsData");
    await Promise.all([this.fetchGwasFiles(), this.fetchLdFiles()]);
    await this.fetchAlgorithmsFromPB();
  },
};
</script>

<style scoped>
.step-indicator {
  width: 28px; height: 28px;
  display: flex; align-items: center; justify-content: center;
  background-color: #5c60f5; color: #fff;
  border-radius: 6px; font-weight: 800; font-size: 0.7rem;
}
.pipeline-flow-line {
  height: 30px;
  width: 2px;
  border-left: 2px dashed #dee2e6;
  margin-left: 13px;
}
.custom-scrollbar::-webkit-scrollbar { width: 4px; height: 4px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e0; border-radius: 10px; }
.bg-soft-primary { background: rgba(92, 96, 245, 0.12); }
main { display: flex; flex-direction: column; }
</style>
